import { Element, ElementType } from './element';
import { Events } from './events';
import { Mesh } from './mesh';
import { Scene } from './scene';
import { Splat } from './splat';

type SelectionElement = Splat | Mesh | Element;

const registerSelectionEvents = (events: Events, scene: Scene) => {
    let selection: SelectionElement = null;
    let importInProgress = false;

    const setSelection = (element: SelectionElement) => {
        // Some selectable elements (e.g. GlobalClipBoxElement) are not splats/meshes and
        // do not expose a `visible` property. Treat those as selectable by default.
        const visible = !element ? true : (element as any).visible;
        const selectable = visible === undefined ? true : !!visible;

        if (element !== selection && selectable) {
            const prev = selection;
            selection = element;
            events.fire('selection.changed', selection, prev);
        }
    };

    events.on('selection', (element: SelectionElement) => {
        setSelection(element);
    });

    events.on('scene.importStarted', () => {
        importInProgress = true;
    });

    events.on('scene.importFinished', () => {
        importInProgress = false;
    });

    events.function('selection', () => {
        return selection;
    });

    events.on('selection.next', () => {
        const elements = [
            ...scene.getElementsByType(ElementType.splat),
            ...scene.getElementsByType(ElementType.model),
            ...scene.getElementsByType(ElementType.helper),
            ...scene.getElementsByType(ElementType.clipBox)
        ] as SelectionElement[];

        if (elements.length > 1) {
            const idx = elements.indexOf(selection);
            setSelection(elements[(idx + 1) % elements.length]);
        }
    });

    events.on('scene.elementAdded', (element: Element) => {
        if (importInProgress) return;
        // Do not auto-select the Global Clip Box element when it's added during Scene construction.
        // Auto-selecting it makes the first user click on the clip box row a no-op (selection doesn't
        // change), which can short-circuit first-interaction transform binding.
        if (element.type === ElementType.splat || element.type === ElementType.model) {
            setSelection(element as SelectionElement);
        }

        // For vehicle motion imports, prefer auto-selecting the non-animated root helper
        // so users can align the imported vehicle without fighting animation.
        if (element.type === ElementType.helper) {
            const animated = !!(element as any).animated;
            const name = String((element as any).name ?? '');
            if (!animated && name.endsWith('_root')) {
                setSelection(element as SelectionElement);
            }
        }
    });

    events.on('scene.elementRemoved', (element: Element) => {
        if (element === selection) {
            const elements = [
                ...scene.getElementsByType(ElementType.splat),
                ...scene.getElementsByType(ElementType.model),
                ...scene.getElementsByType(ElementType.helper),
                ...scene.getElementsByType(ElementType.clipBox)
            ] as SelectionElement[];
            setSelection(elements.length === 0 ? null : elements[0]);
        }
    });

    events.on('splat.visibility', (splat: Splat) => {
        if (splat === selection && !splat.visible) {
            setSelection(null);
        }
    });

    events.on('mesh.visibility', (mesh: Mesh) => {
        if (mesh === selection && !mesh.visible) {
            setSelection(null);
        }
    });

    events.on('helper.visibility', (helper: any) => {
        if (helper === selection && !(helper as any).visible) {
            setSelection(null);
        }
    });

    events.on('camera.focalPointPicked', (details: { splat: Splat, mesh: Mesh }) => {
        const pivot = events.invoke('pivot') as { active?: boolean; end?: () => void } | null;
        if (pivot?.active) {
            // Recover from stale active pivot states (e.g. missed pointer-up) so object picks
            // continue to update selection without requiring click-off/click-on rituals.
            try {
                pivot.end?.();
            } catch (e) {
                // best effort
            }
        }

        if (details.splat) {
            setSelection(details.splat);
        } else if (details.mesh) {
            setSelection(details.mesh);
        }
    });
};

export { registerSelectionEvents };
